// utils/saleFeeHydration.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- pure fee-intent + draft-hydration state
// machine for SaleEntry. Kept framework-independent (like the *Idempotency.ts
// modules) so the intent-provenance rules (Condition 1 + Finding 2) are
// unit-testable without rendering the form or mocking async reference-data
// fetches.
//
// Governing invariant:
//   * an automatic effect firing (payment method / amount / async
//     paymentMethods arrival) NEVER changes explicit intent -- whether that
//     explicit intent was RESTORED from a draft or freshly ENTERED by the user;
//   * ONLY a genuine user amount/method change, or clearing the Fee field,
//     returns intent to configured.
//
// Because "explicit" itself is preserved across every auto-calc firing, no
// separate "lock" flag is needed: the mode is the whole state. This also fixes
// the earlier bug where a newly-typed explicit fee (lock=false) was reset to
// configured by the next auto-calc firing.

export type FeeMode = "configured" | "explicit";

export type FeeIntentState = {
  mode: FeeMode;
};

/** Initial state from a restored draft's persisted fee mode (or null for a
 * pre-2B / fresh draft that has no persisted mode -- adopts configured). */
export function initFeeIntentFromDraft(restoredMode: FeeMode | null): FeeIntentState {
  return { mode: restoredMode === "explicit" ? "explicit" : "configured" };
}

/** The auto-calc effect fired (payment method / amount / async paymentMethods
 * arrival). It NEVER changes explicit intent; a configured state stays
 * configured (the effect just recomputes the non-authoritative preview). */
export function reduceOnAutoCalc(s: FeeIntentState): FeeIntentState {
  return s.mode === "explicit" ? s : { mode: "configured" };
}

/** The user edited the Fee input. `nonBlank` == the field now holds a value
 * (including a typed 0) -> explicit; a cleared field -> configured. */
export function reduceOnUserFeeEdit(s: FeeIntentState, nonBlank: boolean): FeeIntentState {
  void s;
  return { mode: nonBlank ? "explicit" : "configured" };
}

/** A genuine user-driven amount or payment-method change: resets to configured
 * and recalculates, matching current behavior. */
export function reduceOnUserAmountOrMethodChange(s: FeeIntentState): FeeIntentState {
  void s;
  return { mode: "configured" };
}
